import React, {useCallback, useEffect, useState} from 'react';
import isEqual from 'react-fast-compare';
import {Chart, ChartPostData, ChartQuery, ChartSettings} from 'entities/chart';
import {BaseModal} from 'components/modals';
import Container from 'react-bootstrap/Container';
import ChartSettingsForm from '@/views/storyboard/chart_settings/ChartSettingsForm';
import {ChartSettingsQueryPanelRef} from '@/views/storyboard/chart_settings/ChartSettingsQueryPanel';
import Button from '@/components/ui/Button';
import axios, {AxiosResponse} from 'axios';
import {useAlert} from '@/components/alerts';
import {useFormik} from 'formik';
import {
    chartQueryFormSchema
} from '@/views/storyboard/chart_settings/chartSettingsFormValidationSchema';
import ChartCommentSettingsForm, {
    ChartCommentSettingsSchema
} from '@/views/storyboard/chart_settings/ChartCommentSettingsForm';
import i18n from 'i18n';
import {useStoreActions, useStoreState} from 'store';
import useStoryboardsData from '@/views/storyboard/hooks/useStoryboardsData';
import {ChartSettingsTitleEditorRef} from '@/views/storyboard/chart_settings/ChartSettingsTitleEditor';
import {ChartDescriptionInputRef} from '@/views/storyboard/chart_settings/ChartDescriptionInput';
import ChartMoveChartButton from '@/views/storyboard/chart_settings/ChartMoveChartButton';
import {useRenderDiagnostics} from '@/hooks/useRenderDiagnostics';
import {ChartType} from '@/views/storyboard/utils/chartConstants';
import {getChartSettingsCompatibilityHints} from '@/views/storyboard/chart_settings/chartSettingsCompatibilityHints';

interface Props {
    initialChartQueryValues: ChartQuery;
    initialChartSettingsValues: ChartSettings;
    comment: boolean;
    setOnModalClose: (onModalClose: () => void) => void;
    confirmationOpen: boolean;
    setConfirmationOpen: (open: boolean) => void;
    onModalHide: () => void;
    onSave: (chartData: Chart) => void;
}

const ChartSettingsModalContents = React.memo<Props>((
    {
        initialChartSettingsValues,
        initialChartQueryValues,
        comment,
        onSave,
        setOnModalClose,
        confirmationOpen,
        setConfirmationOpen,
        onModalHide,
    }
) => {
    const {showSuccess, showError} = useAlert();
    const changeItemType = comment ? i18n.t('ch_comment.text') : i18n.t('ch_chart.text');

    // Add storyboard reload functionality
    const setStoryboardsReload = useStoreActions((actions) => actions.storyboards.setReload);
    const activeStoryboardId = useStoreState((state) => state.activeStoryboard.activeId);
    const [, , setStoryboards] = useStoryboardsData();

    const [submitted, setSubmitted] = useState<boolean>(false);
    const [titleSaveAttempted, setTitleSaveAttempted] = useState<boolean>(false);
    const chartTitleInputRef = React.useRef<ChartSettingsTitleEditorRef>(null);
    const chartDescriptionInputRef = React.useRef<ChartDescriptionInputRef>(null);
    const chartQueryPanelRef = React.useRef<ChartSettingsQueryPanelRef>(null);

    const {id: chartId} = initialChartSettingsValues;


    if (comment) {
        initialChartQueryValues.query_string = '*';
    }

    const chartQueryFormik = useFormik<ChartQuery>({
        initialValues: initialChartQueryValues,
        validationSchema: chartQueryFormSchema,
        validateOnMount: false,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: () => {
        },
    });

    const chartSettingsFormik = useFormik<ChartSettings>({
        initialValues: initialChartSettingsValues,
        validationSchema: ChartCommentSettingsSchema,
        validateOnMount: false,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: () => {
        },
    });

    useRenderDiagnostics('ChartSettingsModalContents', {
        chartType: chartSettingsFormik.values.type,
        output: chartQueryFormik.values.output,
        chartId: initialChartSettingsValues.id,
    });

    const chartNameIsPresent = (chartSettingsFormik.values.title || '').trim().length > 0;
    const showTitleRequiredError = titleSaveAttempted && !chartNameIsPresent;

    const [formIsDirty, setFormIsDirty] = useState(false);
    const [formHasErrors, setFormHasErrors] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setFormIsDirty(
                !isEqual(initialChartSettingsValues, chartSettingsFormik.values)
                || !isEqual(initialChartQueryValues, chartQueryFormik.values),
            );
        }, 100);

        return () => clearTimeout(timeout);
    }, [
        initialChartSettingsValues,
        chartSettingsFormik.values,
        initialChartQueryValues,
        chartQueryFormik.values,
    ]);

    useEffect(() => {
        const timeout = setTimeout(async () => {
            const [queryErrors, settingsErrors] = await Promise.all([
                chartQueryFormik.validateForm(chartQueryFormik.values),
                chartSettingsFormik.validateForm(chartSettingsFormik.values),
            ]);

            const blockingSettingsErrorKeys = Object.keys(settingsErrors || {}).filter((key) => key !== 'title');

            setFormHasErrors(
                Object.keys(queryErrors || {}).length > 0
                || blockingSettingsErrorKeys.length > 0,
            );
        }, 300);

        return () => clearTimeout(timeout);
    }, [chartQueryFormik.values, chartSettingsFormik.values]);

    const compatibilityBlocksSave = !comment && getChartSettingsCompatibilityHints(
        chartSettingsFormik.values.type as ChartType,
        chartQueryFormik.values,
    ).some((hint) => hint.variant === 'error');

    const saveDisabled = submitted || formHasErrors || compatibilityBlocksSave;

    const flushPendingFormUpdates = useCallback(() => {
        chartTitleInputRef.current?.flushPendingUpdate();
        chartDescriptionInputRef.current?.flushPendingUpdate();
        chartQueryPanelRef.current?.flushPendingQuery();
    }, []);

    const computeFormIsDirty = useCallback(() => (
        !isEqual(initialChartSettingsValues, chartSettingsFormik.values)
        || !isEqual(initialChartQueryValues, chartQueryFormik.values)
    ), [
        initialChartSettingsValues,
        chartSettingsFormik.values,
        initialChartQueryValues,
        chartQueryFormik.values,
    ]);

    const onModalClose = useCallback(() => {
        flushPendingFormUpdates();

        if (computeFormIsDirty()) {
            setConfirmationOpen(true);
        } else {
            onModalHide();
        }
    }, [computeFormIsDirty, flushPendingFormUpdates, setConfirmationOpen, onModalHide]);

    useEffect(() => {
        setOnModalClose(() => onModalClose);
    }, [setOnModalClose, onModalClose]);

    const onModalDiscard = () => {
        setConfirmationOpen(false);
        onModalHide();
    };

    const onModalSave = async () => {
        flushPendingFormUpdates();

        await new Promise(resolve => setTimeout(resolve, 10));

        if (!(chartSettingsFormik.values.title || '').trim()) {
            setTitleSaveAttempted(true);
            chartSettingsFormik.setFieldTouched('title', true);
            return;
        }

        const [queryErrors, settingsErrors] = await Promise.all([
            chartQueryFormik.validateForm(chartQueryFormik.values),
            chartSettingsFormik.validateForm(chartSettingsFormik.values),
        ]);

        const hasQueryErrors = queryErrors && Object.keys(queryErrors).length > 0;
        const hasSettingsErrors = settingsErrors && Object.keys(settingsErrors).length > 0;

        if (hasQueryErrors || hasSettingsErrors) {
            const settingsErrorKeys = Object.keys(settingsErrors || {});
            const queryErrorKeys = Object.keys(queryErrors || {});
            const firstErrorKey = settingsErrorKeys[0] || queryErrorKeys[0];
            if (firstErrorKey) {
                if (settingsErrorKeys.includes(firstErrorKey)) {
                    chartSettingsFormik.setFieldTouched(firstErrorKey as keyof ChartSettings, true);
                } else if (queryErrorKeys.includes(firstErrorKey)) {
                    chartQueryFormik.setFieldTouched(firstErrorKey as keyof ChartQuery, true);
                }
            }
            return;
        }
        setConfirmationOpen(false);

        const chartQueryData = chartQueryFormik?.values;
        const chartSettingsData = chartSettingsFormik?.values;

        if (!chartQueryData || !chartSettingsData) {
            console.error('❌ Missing form data:', {chartQueryData, chartSettingsData});
            showError(i18n.t('ch_something_went_wrong_refresh.text'));
            return;
        }

        setSubmitted(true);

        const postData: ChartPostData = {
            search_query: chartQueryData,
            ...chartSettingsData,
            ignoreGlobalFilters: false,
        };

        if (typeof initialChartQueryValues.id !== 'undefined') {
            postData.chartQueryId = initialChartQueryValues.id;
        }

        if (typeof initialChartSettingsValues.id !== 'undefined') {
            postData.chartId = initialChartSettingsValues.id;
        }

        try {
            const response = await axios.post<Chart, AxiosResponse<Chart, ChartPostData>, ChartPostData>(window.URLS.chartQueries, postData);

            // Determine if storyboard reload is needed
            const isNewChart = !initialChartSettingsValues.id;
            const isMovingToNewStoryboard = response.data.storyboardId !== activeStoryboardId;
            const shouldReloadStoryboard = isNewChart || isMovingToNewStoryboard;

            try {
                onSave(response.data);

                // Trigger storyboard reload if needed
                if (shouldReloadStoryboard) {
                    try {
                        // Primary reload method using store action
                        setStoryboardsReload(true);

                        // Fallback reload method using useData hook
                        // This ensures data consistency even if the primary method fails
                        setTimeout(() => {
                            try {
                                setStoryboards(true);
                            } catch (fallbackError) {
                                console.error('❌ Fallback reload also failed:', fallbackError);
                            }
                        }, 100);

                    } catch (reloadError) {
                        console.error('❌ Error triggering storyboard reload:', reloadError);
                        // Don't fail the save operation for reload errors
                    }
                } else {
                }

                showSuccess(i18n.t('ch_succ_saved.text', {changeItemType: changeItemType}));
            } catch (callbackError) {
                console.error('❌ CRITICAL ERROR in onSave callback:', callbackError);
                if (callbackError instanceof Error) {
                    console.error('Callback error details:', {
                        message: callbackError.message,
                        stack: callbackError.stack,
                        name: callbackError.name
                    });
                }
                showError(i18n.t('chart_settings_save_callback_error.text'));
                setSubmitted(false);
                // Don't re-throw to prevent page reload
            }
        } catch (error) {
            console.error('❌ Error saving chart:', error);
            showError(i18n.t('ch_something_went_wrong.text'));
            setSubmitted(false);
            // Don't re-throw to prevent page reload
        }
    };

    return (
        <>
            <div className="modal-header" data-cy="chart-settings-modal-header">
                <h4 className="modal-title" id="contained-modal-title-vcenter">
                    {`${changeItemType} ${i18n.t('comment_settings_title.text')}`}
                    {/* This is for query link logic */}
                    {/* {`${changeItemType} Settings (ID: ${chartId})`} */}
                </h4>
                <button
                    type="button"
                    className="close"
                    aria-label={i18n.t('jt_close.text')}
                    onClick={onModalClose}
                    data-cy="chart-settings-contents-btn-2"
                >
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <div className="modal-body chart-settings-modal-body">
                <Container fluid>
                    {
                        comment
                            ? (
                                <ChartCommentSettingsForm
                                    chartId={chartId}
                                    chartQueryFormik={chartQueryFormik}
                                    chartSettingsFormik={chartSettingsFormik}
                                    showTitleRequiredError={showTitleRequiredError}
                                />
                            ) : (
                                <ChartSettingsForm
                                    chartQueryFormik={chartQueryFormik}
                                    chartSettingsFormik={chartSettingsFormik}
                                    chartTitleInputRef={chartTitleInputRef}
                                    chartDescriptionInputRef={chartDescriptionInputRef}
                                    chartQueryPanelRef={chartQueryPanelRef}
                                    showTitleRequiredError={showTitleRequiredError}
                                />
                            )}
                </Container>
            </div>
            <div className="modal-footer chart-settings-modal-footer">
                {
                    !comment ? (
                        <ChartMoveChartButton chartSettingsFormik={chartSettingsFormik} />
                    ) : null
                }
                <div className="chart-settings-modal-footer__save">
                    {formIsDirty && !submitted ? (
                        <span className="chart-settings-modal-footer__unsaved">
                            {i18n.t('chart_settings_unsaved_changes.text')}
                        </span>
                    ) : null}
                    <Button
                        variant="info"
                        className={`save-chart-btn${formIsDirty ? ' save-chart-btn--dirty' : ''}`}
                        onClick={() => onModalSave()}
                        data-cy="save-btn"
                        disabled={saveDisabled}
                    >
                        {i18n.t('jt_save.text')}
                    </Button>
                </div>
            </div>

            <BaseModal
                isOpen={confirmationOpen}
                onClose={() => setConfirmationOpen(false)}
                title={i18n.t('jt_save_chart.text')}
                footer={
                    <>
                        <Button
                            data-dismiss="alert"
                            onClick={() => onModalDiscard()}
                            variant="secondary"
                            data-cy="chart-discard-modal-discard-button"
                        >
                            {i18n.t('jt_discard.text')}
                        </Button>
                        <Button
                            onClick={() => onModalSave()}
                            variant="info"
                            data-cy="chart-discard-modal-save-button"
                            disabled={saveDisabled}
                        >
                            {i18n.t('jt_save.text')}
                        </Button>
                    </>
                }
            >
                {i18n.t('jt_any_changes.text')}
            </BaseModal>
        </>
    );
});

export {ChartSettingsModalContents};